import { getAuth, isJwtExpired, parseJwt } from '@envision/ev-ui-component-library'
import { logger } from '@sentry/react'

const ignoreSSOKey = 'ignore_sso'

let syncTokens = null

const syncFromTokens = (tokens) => {
  syncTokens = tokens
    ? {
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
        expires_in: tokens.expires_in,
        refresh_expires_in: tokens.refresh_expires_in,
      }
    : null
}

const getServices = () => {
  try {
    return getAuth()
  } catch (error) {
    logger.error('Auth is not configured', error)
    return null
  }
}

/** Preload tokens into the sync cache (call once before rendering the app). */
export const hydrateAuthTokenCache = async () => {
  const services = getServices()
  if (!services) return null

  const { tokenService, ssoService } = services
  await ssoService.initializeFromSSO()
  const tokens = await tokenService.load()
  syncFromTokens(tokens)
  return tokens
}

const Token = {}

Token.getToken = () => syncTokens?.access_token ?? null

Token.getRefreshToken = () => syncTokens?.refresh_token ?? null

Token.setAuthTokens = (authObject) => {
  syncFromTokens(authObject)
  const services = getServices()
  if (services) {
    void services.tokenService.setTokens(authObject)
  }
}

Token.removeAuthTokens = () => {
  syncTokens = null
  const services = getServices()
  if (services) {
    services.tokenService.clear()
    services.ssoService.clearSSO()
  }
}

Token.removeLocalStorageTokensOnly = () => {
  syncTokens = null
  const services = getServices()
  if (services) {
    services.tokenService.clear()
  }
}

const tokenIsExpired = (expiresIn) => {
  if (!expiresIn) return true
  const expiresAt = Math.floor(Date.now() / 1000) + expiresIn
  return expiresAt <= Math.floor(Date.now() / 1000)
}

Token.tokenIsValid = () => {
  if (!syncTokens?.access_token) return false
  return !tokenIsExpired(syncTokens.expires_in)
}

Token.refreshTokenIsValid = () => {
  if (!syncTokens?.refresh_token) return false
  return !tokenIsExpired(syncTokens.refresh_expires_in)
}

Token.getSSoToken = () => {
  const services = getServices()
  return services?.ssoService.getSSOToken() ?? null
}

Token.setIgnoreSSO = () => {
  try {
    window.sessionStorage.setItem(ignoreSSOKey, '1')
  } catch (error) {
    logger.error('Failed to set ignore SSO', error)
  }
}

Token.clearIgnoreSSO = () => {
  try {
    window.sessionStorage.removeItem(ignoreSSOKey)
  } catch (error) {
    logger.error('Failed to clear ignore SSO', error)
  }
}

Token.isSSOIgnored = () => {
  try {
    return window.sessionStorage.getItem(ignoreSSOKey) === '1'
  } catch (error) {
    logger.error('Failed to read ignore SSO flag', error)
    return false
  }
}

Token.bootstrapFromSSOIfAvailable = () => {
  const services = getServices()
  if (!services || Token.isSSOIgnored()) return null

  const sso = services.ssoService.getSSOToken()
  if (!sso || isJwtExpired(sso)) return null

  let expiresIn = 3600
  const payload = parseJwt(sso)
  if (payload?.exp) {
    const nowSec = Math.floor(Date.now() / 1000)
    expiresIn = Math.max(0, payload.exp - nowSec)
  }

  const authObject = {
    access_token: sso,
    refresh_token: sso,
    token_type: 'Bearer',
    expires_in: expiresIn,
    refresh_expires_in: expiresIn,
    scope: payload?.scope || '',
  }

  Token.setAuthTokens(authObject)
  return authObject
}

export default Token
